import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    ArrowSwapHorizontal,
    BitcoinConvert,
    EmptyWallet,
    Eye,
    EyeSlash,
    Global,
    Money,
    Send2,
} from "iconsax-reactjs";
import USDCIcon from "asset/icons/USDC.svg";
import USDTIcon from "asset/icons/USDT.svg";
import NGNFlagImg from "asset/images/Currency.svg";
import GBPFlagImg from "asset/images/GB.svg";
import USFlagImg from "asset/images/US.png";
import EUFlagImg from "asset/images/EU.svg";
import { useNavigate } from "react-router-dom";

const CRYPTO_CURRENCIES = ["USDT", "USDC"];

const getFiatFlagImg = (currency) => {
    switch (String(currency || "").toUpperCase()) {
        case "USD":
            return USFlagImg;
        case "EUR":
            return EUFlagImg;
        case "GBP":
            return GBPFlagImg;
        case "NGN":
        default:
            return NGNFlagImg;
    }
};

const normalizeStandard = (tokenStandard = "") => {
    const raw = String(tokenStandard || "").toUpperCase();
    if (!raw) return "";
    if (raw === "TRC20") return "TRC-20";
    if (raw === "ERC20") return "ERC-20";
    return raw;
};

const toTitle = (value = "") =>
    String(value || "")
        .split(/[_\s-]+/)
        .filter(Boolean)
        .map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
        .join(" ");

const networkSubtitle = (provider, currency) => {
    const network = provider?.network || provider?.asset?.name || provider?.symbol || "";
    const networkName = toTitle(network || "Network");
    const standardFromAsset = normalizeStandard(provider?.asset?.tokenStandard);
    const inferredStandard =
        !standardFromAsset &&
        ["ethereum", "polygon", "base"].includes(String(network || "").toLowerCase()) &&
        ["USDC", "USDT"].includes(String(currency || "").toUpperCase())
            ? "ERC-20"
            : "";
    const standard = standardFromAsset || inferredStandard;
    return `${networkName}${standard ? ` (${standard})` : ""}`;
};

const formatBalance = (value) =>
    Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const getRows = (wallet, isCryptoCcy) => {
    const providers = Array.isArray(wallet?.providers) ? wallet.providers : [];
    const grouped = new Map();

    providers.forEach((provider) => {
        const keyRaw = isCryptoCcy
            ? provider?.network ||
              provider?.asset?.name ||
              provider?.symbol ||
              provider?.asset?.symbol ||
              provider?._id
            : provider?._id || provider?.accountNumber || provider?.bankName || provider?.bank;
        const key = String(keyRaw || "").toLowerCase() || provider?._id;
        if (!key) return;

        const current = grouped.get(key);
        const currentTs = new Date(current?.updatedAt || 0).getTime();
        const nextTs = new Date(provider?.updatedAt || 0).getTime();
        const currentHasBalance = Number.isFinite(Number(current?.balance));
        const nextHasBalance = Number.isFinite(Number(provider?.balance));

        if (!current) {
            grouped.set(key, provider);
            return;
        }

        if (nextHasBalance && !currentHasBalance) {
            grouped.set(key, provider);
            return;
        }

        if (nextTs >= currentTs) grouped.set(key, provider);
    });

    return Array.from(grouped.values())
        .map((provider) =>
            isCryptoCcy
                ? {
                      id: provider?._id,
                      balance: Number(provider?.balance || 0),
                      subtitle: networkSubtitle(provider, wallet?.currency),
                      logo: provider?.asset?.logoUrl || "",
                      networkCode:
                          provider?.network || provider?.asset?.name || provider?.symbol || "",
                      accountNumber: "",
                  }
                : {
                      id: provider?._id || provider?.accountNumber,
                      balance: Number(provider?.balance || 0),
                      subtitle: [provider?.bankName || provider?.bank, provider?.accountNumber]
                          .filter(Boolean)
                          .join(" • "),
                      logo: "",
                      networkCode: provider?.bankName || provider?.bank || "",
                      accountNumber: provider?.accountNumber || "",
                  }
        )
        .sort((a, b) => b.balance - a.balance);
};

export default function AccountBalancesDrawer({ isOpen, onClose, wallet, currency }) {
    const [showBalance, setShowBalance] = useState(true);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();

    const ccy = String(currency || wallet?.currency || "").toUpperCase();
    const isCryptoCcy = CRYPTO_CURRENCIES.includes(ccy);

    const rows = useMemo(() => getRows(wallet, isCryptoCcy), [wallet, isCryptoCcy]);
    const totalBalance = useMemo(() => {
        const walletBalance = Number(wallet?.balance);
        if (Number.isFinite(walletBalance)) return walletBalance;
        return rows.reduce((sum, row) => sum + Number(row.balance || 0), 0);
    }, [wallet?.balance, rows]);

    useEffect(() => {
        if (!isOpen) setActiveDropdown(null);
    }, [isOpen]);

    useEffect(() => {
        const onDown = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setActiveDropdown(null);
            }
        };
        if (activeDropdown) document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [activeDropdown]);

    if (!isOpen) return null;

    const icon = isCryptoCcy ? (ccy === "USDT" ? USDTIcon : USDCIcon) : getFiatFlagImg(ccy);
    const sendOptions = [
        {
            title: `Send ${ccy} to a wallet`,
            subtitle: `Send ${ccy} to any crypto wallet address`,
            icon: <BitcoinConvert size="18" color="#292D32" />,
            onClick: () => {
                setActiveDropdown(null);
                onClose?.();
                navigate("/payment/send-crypto/crypto-wallet/amount");
            },
        },
        {
            title: `Send ${ccy} to fiat`,
            subtitle: `Send ${ccy} to other fiat currencies`,
            icon: <Money size="18" color="#292D32" />,
            onClick: () => {
                setActiveDropdown(null);
                onClose?.();
                navigate("/payment/send-crypto/fiat-wallet/amount");
            },
        },
    ];

    const convertOptions = [
        {
            title: "Convert to another network",
            subtitle: `Move ${ccy} from one network to another`,
            icon: <BitcoinConvert size="18" color="#6F739B" />,
            onClick: () => {
                setActiveDropdown(null);
                onClose?.();
                navigate("/convert-crypto?mode=network");
            },
        },
        {
            title: "Convert to another crypto currency",
            subtitle: `Convert ${ccy} to your other stablecoin`,
            icon: <EmptyWallet size="18" color="#6F739B" />,
            onClick: () => {
                setActiveDropdown(null);
                onClose?.();
                navigate("/convert-crypto?mode=currency");
            },
        },
        {
            title: "Convert to fiat account",
            subtitle: `Convert ${ccy} to NGN, USD, KES and more`,
            icon: <Money size="18" color="#6F739B" />,
            disabled: true,
        },
    ];

    const fiatSendOptions = [
        {
            title: "Same currency transfer",
            subtitle: "Send money within the same currency",
            icon: <Money size="18" color="#292D32" />,
            onClick: () => {
                setActiveDropdown(null);
                onClose?.();
                navigate("/payment/send-money", {
                    state: { type: "same", default: ccy, balance: totalBalance },
                });
            },
        },
        {
            title: "Cross-border transfer",
            subtitle: "Send money between different currencies",
            icon: <Global size="18" color="#9CA3AF" />,
            onClick: () => {
                setActiveDropdown(null);
                onClose?.();
                navigate("/payment/send-money/cross-border/bank-recipient/amount", {
                    state: { default: ccy, balance: totalBalance },
                });
            },
        },
    ];

    const fiatConvertOptions = [
        {
            title: "Convert currency",
            subtitle: `Convert ${ccy} to another currency`,
            icon: <ArrowSwapHorizontal size="18" color="#6F739B" />,
            onClick: () => {
                setActiveDropdown(null);
                onClose?.();
                navigate("/convert-currency");
            },
        },
    ];

    return (
        <div>
            <div className="fixed inset-0 bg-black/25 z-[300]" onClick={onClose} />

            <div
                className="fixed inset-y-0 right-0 w-full max-w-lg rounded-tl-xl rounded-bl-xl bg-white shadow-lg z-[350] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-6 border-b">
                    <div className="flex items-center gap-3">
                        <img src={icon} alt={`${ccy} icon`} className="w-8 h-8" />
                        <h2 className="text-[24px] font-[600] mb-0">{ccy} Account balances</h2>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        ✕
                    </button>
                </div>

                <div className="p-6 ">
                    <div className="border border-[#E5E7F6] rounded-[12px] p-1">
                        <div className="rounded-[12px] bg-[#F7F8FD] p-3 mb-4">
                            <p className="text-[14px] font-[400] text-[#6F739B]">Total balance</p>
                            <div className="flex items-center gap-2 mt-1">
                                <p className="text-[24px] font-[450] text-[#161827]">
                                    {showBalance ? formatBalance(totalBalance) : "••••••"}
                                </p>
                                <p className="text-[14px] font-[350] text-[#383B56]">{ccy}</p>
                                <button
                                    onClick={() => setShowBalance((v) => !v)}
                                    className="ml-1 mb-1"
                                    type="button"
                                >
                                    {showBalance ? (
                                        <EyeSlash size="20" color="#27293F" />
                                    ) : (
                                        <Eye size="20" color="#27293F" />
                                    )}
                                </button>
                            </div>
                            {/* <p className="text-[#6F739B] text-sm mt-1">
                                {showBalance ? `≈ $${formatBalance(totalBalance)}` : "≈ $••••••"}
                            </p> */}
                        </div>

                        <div className="space-y-4 px-4 py-2">
                            {rows.map((row) => (
                                <div
                                    key={row.id}
                                    className="flex items-center justify-between gap-3"
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        {row.logo ? (
                                            <div className="w-12 h-12 rounded-full bg-[#F7F8FD] flex items-center justify-center">
                                                <img
                                                    src={row.logo}
                                                    alt={row.networkCode || "network"}
                                                    className="w-6 h-6 rounded-full object-cover bg-[#F2F4FA]"
                                                />
                                            </div>
                                        ) : (
                                            <div className="w-12 h-12 rounded-full bg-[#F7F8FD] flex items-center justify-center text-sm text-[#667085] uppercase">
                                                {String(row.networkCode || "N").slice(0, 2)}
                                            </div>
                                        )}
                                        <div className="min-w-0 text-[16px] font-[450] text-[#161827]">
                                            <p>
                                                {showBalance
                                                    ? formatBalance(row.balance)
                                                    : "••••••"}{" "}
                                                <span>{ccy}</span>
                                            </p>
                                            <p className="text-[#6F739B] text-[14px] font-[350]">
                                                {row.subtitle}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="relative flex items-center gap-3 shrink-0">
                                        <button
                                            type="button"
                                            className="w-9 h-9 rounded-full border border-[#DCE1F5]"
                                            onClick={() =>
                                                setActiveDropdown((prev) =>
                                                    prev?.rowId === row.id && prev?.type === "send"
                                                        ? null
                                                        : { rowId: row.id, type: "send" }
                                                )
                                            }
                                        >
                                            <Send2 size="16" color="#292D32" />
                                        </button>

                                        <button
                                            type="button"
                                            className="w-9 h-9 rounded-full border border-[#DCE1F5]"
                                            onClick={() =>
                                                setActiveDropdown((prev) =>
                                                    prev?.rowId === row.id &&
                                                    prev?.type === "convert"
                                                        ? null
                                                        : { rowId: row.id, type: "convert" }
                                                )
                                            }
                                        >
                                            <ArrowSwapHorizontal size="16" color="#292D32" />
                                        </button>

                                        {activeDropdown?.rowId === row.id && (
                                            <div
                                                ref={dropdownRef}
                                                className="absolute top-full right-0 mt-2 z-50 w-[min(90vw,400px)] max-w-[calc(100vw-2rem)] sm:w-[400px] bg-white shadow-dropdown rounded-xl border border-[#F1F1F1]"
                                            >
                                                {(activeDropdown.type === "send"
                                                    ? isCryptoCcy
                                                        ? sendOptions
                                                        : fiatSendOptions
                                                    : isCryptoCcy
                                                    ? convertOptions
                                                    : fiatConvertOptions
                                                ).map((item) => (
                                                    <div
                                                        key={item.title}
                                                        className={`flex items-start border m-2 rounded-lg gap-3 p-3 ${
                                                            item.disabled
                                                                ? "opacity-50 cursor-not-allowed"
                                                                : "hover:bg-[#F9FAFB] cursor-pointer"
                                                        }`}
                                                        onClick={
                                                            item.disabled ? undefined : item.onClick
                                                        }
                                                    >
                                                        <div className="w-5 h-5 mt-1">
                                                            {item.icon}
                                                        </div>
                                                        <div className="flex-1">
                                                            <p className="font-semibold text-main">
                                                                {item.title}
                                                            </p>
                                                            <p className="text-[#6B7280] text-sm">
                                                                {item.subtitle}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
